'use client'

import { useRouter } from 'next/navigation'
import * as React from 'react'

/**
 * Custom hook for handling hashtag clicks in HTML content
 * @param contentRef - Ref to the HTML container element
 * @returns Object with handleHashtagClick function
 */
export function useHashtagClickHandler(
  contentRef: React.RefObject<HTMLElement>,
  onHashtagClick?: (hashtag: string) => void
) {
  const router = useRouter()

  const handleHashtagClick = React.useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement

      const hashtagElement = target.closest('.ql-hashtag') as HTMLElement

      if (hashtagElement) {
        event.preventDefault()
        event.stopPropagation()

        const hashtagText = hashtagElement.textContent || hashtagElement.innerText

        if (hashtagText) {
          const hashtagTextClean = hashtagText.replace('#', '')
          if (hashtagTextClean) {
            const currentUrl = new URL(window.location.href)
            currentUrl.searchParams.delete('tags')
            currentUrl.searchParams.set('tags', hashtagTextClean)
            router.push(currentUrl.pathname + currentUrl.search, {})
            onHashtagClick?.(hashtagTextClean)
          }
        }
      }
    },
    [router, onHashtagClick]
  )

  React.useEffect(() => {
    const contentElement = contentRef.current
    if (!contentElement) return

    contentElement.addEventListener('click', handleHashtagClick)

    return () => {
      contentElement.removeEventListener('click', handleHashtagClick)
    }
  }, [handleHashtagClick, contentRef])

  return { handleHashtagClick }
}
